// Orderbevestiging- en akkoord-I/O (epic 2). Additive api-split, ge-barreld via api.ts.
import { supabase } from '../supabase';
import { buildConfirmationSnapshot, type ConfirmationSnapshot } from '../orderConfirmation';
import { calculateGiftPackagePricing } from '../packagePricing';
import type { GiftPackage } from '../types';

const PACKAGE_SELECT =
  '*, items:gift_package_items(*, gift_product:gift_products(*, supplier:gift_suppliers(id, name)))';

export interface ConfirmationPricingGuard {
  id: string;
  pricing_model_version: GiftPackage['pricing_model_version'];
}

export interface ConfirmationDraft {
  snapshot: ConfirmationSnapshot;
  pricingGuard: ConfirmationPricingGuard[];
}

/** Haalt definitieve pakketten met items op + past per-item prijs-override toe (parity met fetchPackagesByProject). */
async function fetchDefinitiefPackages(projectId: string): Promise<GiftPackage[]> {
  const { data, error } = await supabase
    .from('gift_packages')
    .select(PACKAGE_SELECT)
    .eq('project_id', projectId)
    .eq('variant_status', 'definitief');
  if (error) throw error;
  const pkgs = (data as unknown as GiftPackage[]) ?? [];
  for (const pkg of pkgs) {
    for (const it of pkg.items ?? []) {
      if (it.unit_purchase_price_override != null && it.gift_product) {
        it.gift_product = { ...it.gift_product, unit_purchase_price: it.unit_purchase_price_override };
      }
    }
  }
  return pkgs;
}

/**
 * Bouwt de orderbevestiging-snapshot voor een project: aggregeert alle definitieve
 * configuraties. Eindprijs = verkoopprijs incl. btw (selling_incl_btw, gift-pricing).
 * Scope afgeschermd (geen marge/leverancier) — zie buildConfirmationSnapshot.
 */
export async function buildConfirmationDraftForProject(
  projectId: string
): Promise<ConfirmationDraft> {
  const [definitief, project] = await Promise.all([
    fetchDefinitiefPackages(projectId),
    supabase
      .from('projects')
      .select('client:clients(company_name, contact_name)')
      .eq('id', projectId)
      .single(),
  ]);

  const rawClient = project.data?.client as unknown;
  const clientObj = (Array.isArray(rawClient) ? rawClient[0] : rawClient) as
    | { company_name: string; contact_name: string | null }
    | null
    | undefined;
  const client = clientObj ?? { company_name: 'Onbekend', contact_name: null };

  const confirmationPackages = definitief.map((pkg) => ({
    name: pkg.variant_label ?? pkg.name,
    items: (pkg.items ?? [])
      .filter((it) => !it.excluded && it.gift_product)
      .map((it) => ({
        name: it.gift_product!.name,
        quantity: it.quantity_per_package,
      })),
    eindprijs: confirmationSellingInclBtw(pkg),
  }));

  return {
    snapshot: buildConfirmationSnapshot(client, confirmationPackages),
    pricingGuard: definitief
      .map((pkg) => ({ id: pkg.id, pricing_model_version: pkg.pricing_model_version }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  };
}

export async function buildSnapshotForProject(projectId: string): Promise<ConfirmationSnapshot> {
  return (await buildConfirmationDraftForProject(projectId)).snapshot;
}

export function confirmationSellingInclBtw(pkg: GiftPackage): number {
  return calculateGiftPackagePricing(pkg).total.selling_incl_btw;
}

export interface OrderConfirmationRow {
  id: string;
  project_id: string;
  version: number;
  snapshot: ConfirmationSnapshot;
  pdf_path: string | null;
  sent_at: string;
}

/** Legt akkoord vast op het project (mens-event marker). Akkoord is geen voorwaarde voor inkoop. */
export async function setAkkoord(projectId: string): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('projects')
    .update({ akkoord_at: new Date().toISOString(), akkoord_by: auth.user?.id ?? null })
    .eq('id', projectId);
  if (error) throw error;
}

/** Markeert de offerte als verstuurd (mens-event marker, story 2.1). */
export async function markOfferteSent(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .update({ offerte_sent_at: new Date().toISOString() })
    .eq('id', projectId);
  if (error) throw error;
}

export async function fetchConfirmations(projectId: string): Promise<OrderConfirmationRow[]> {
  const { data, error } = await supabase
    .from('gift_order_confirmations')
    .select('id, project_id, version, snapshot, pdf_path, sent_at')
    .eq('project_id', projectId)
    .order('version', { ascending: false });
  if (error) throw error;
  return (data as OrderConfirmationRow[]) ?? [];
}

export async function fetchLatestConfirmation(
  projectId: string
): Promise<OrderConfirmationRow | null> {
  const rows = await fetchConfirmations(projectId);
  return rows[0] ?? null;
}

/**
 * Slaat een nieuwe versie van de orderbevestiging op (append-only). Versie = hoogste + 1;
 * oude versies blijven bestaan (FR-4.6). Aanroepen op de verstuur-actie, niet bij genereren.
 */
export async function createOrderConfirmation(
  projectId: string,
  snapshot: ConfirmationSnapshot,
  pricingGuard: ConfirmationPricingGuard[],
  pdfPath?: string
): Promise<OrderConfirmationRow> {
  const { data, error } = await supabase.rpc('create_gift_order_confirmation', {
    p_project_id: projectId,
    p_snapshot: snapshot,
    p_pdf_path: pdfPath ?? null,
    p_expected_pricing_models: pricingGuard,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Orderbevestiging gaf geen opgeslagen versie terug');
  return row as OrderConfirmationRow;
}
